import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogTitle from "@material-ui/core/DialogTitle";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import TextField from "@material-ui/core/TextField";
import Checkbox from "@mui/material/Checkbox";
import { Stack, Typography, Grid } from "@mui/material";
import Compressor from "compressorjs";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { capitalize } from "../../utils/capitalize";

// Ícones Material UI
import {
  Message,
  AccessTime,
  Image as ImageIcon,
  MicNone,
  Videocam,
  Delete
} from "@mui/icons-material";

// ====== DRAG AND DROP ======
import {
  DragDropContext,
  Droppable,
  Draggable
} from "react-beautiful-dnd";

const FlowBuilderSingleBlockModal = ({
  open,
  onSave,
  onUpdate,
  data,
  close
}) => {
  const isMounted = useRef(true);

  // Controle de estado do modal e loading
  const [activeModal, setActiveModal] = useState(false);
  const [loading, setLoading] = useState(false);

  // Armazena a sequência e os “cards” (React nodes)
  const [elementsSeq, setElementsSeq] = useState([]); 
  const [elements, setElements] = useState([]);       

  // Para edição (caso open === 'edit')
  const [elementsSeqEdit, setElementsSeqEdit] = useState([]);
  const [elementsEdit, setElementsEdit] = useState([]);

  // Estados para contadores
  const [numberMessages, setNumberMessages] = useState(0);
  const [numberMessagesLast, setNumberMessagesLast] = useState(0);

  const [numberInterval, setNumberInterval] = useState(0);
  const [numberIntervalLast, setNumberIntervalLast] = useState(0);

  const [numberImg, setNumberImg] = useState(0);
  const [numberImgLast, setNumberImgLast] = useState(0);

  const [numberAudio, setNumberAudio] = useState(0);
  const [numberAudioLast, setNumberAudioLast] = useState(0);

  const [numberVideo, setNumberVideo] = useState(0);
  const [numberVideoLast, setNumberVideoLast] = useState(0);

  // Medias e previews
  const [medias, setMedias] = useState([]);
  const [previewImg, setPreviewImg] = useState([]);
  const [previewAudios, setPreviewAudios] = useState([]);
  const [previewVideos, setPreviewVideos] = useState([]);

  // Rótulos (novo ou editar)
  const [labels, setLabels] = useState({
    title: "Adicionar conteúdo ao fluxo",
    btn: "Adicionar"
  });

  // ================================
  //   DRAG AND DROP - reordenar
  // ================================
  const handleOnDragEnd = (result) => {
    if (!result.destination) return;
    const newElements = Array.from(elements);
    const [removedItem] = newElements.splice(result.source.index, 1);
    newElements.splice(result.destination.index, 0, removedItem);

    const newSeq = Array.from(elementsSeq);
    const [removedSeq] = newSeq.splice(result.source.index, 1);
    newSeq.splice(result.destination.index, 0, removedSeq);

    setElements(newElements);
    setElementsSeq(newSeq);
  };

  // =====================================
  //  LAYOUTS: Texto, Intervalo, Imagem, Áudio, Vídeo
  // =====================================
  const messageLayout = (num, defaultValue = "") => {
    const key = `message${num}`;
    return (
      <div
        key={key}
        className={`stackMessage${num} message${num}`}
        style={styleCard}
      >
        {/* Botão remover */}
        <div style={styleRemoveIcon}>
          <Delete onClick={() => deleteElementsTypeOne(num, "message")} />
        </div>
        <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
          Texto
        </Typography>
        <TextField
          label="Mensagem"
          defaultValue={defaultValue}
          multiline
          rows={3}
          variant="outlined"
          fullWidth
        />
      </div>
    );
  };

  const intervalLayout = (num, defaultValue = 1) => {
    const key = `interval${num}`;
    return (
      <div
        key={key}
        className={`stackInterval${num} interval${num}`}
        style={styleCard}
      >
        <div style={styleRemoveIcon}>
          <Delete onClick={() => deleteElementsTypeOne(num, "interval")} />
        </div>
        <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
          Intervalo
        </Typography>
        <TextField
          label="Tempo em segundos"
          defaultValue={defaultValue}
          type="number"
          variant="outlined"
          fullWidth
        />
      </div>
    );
  };

  const imgLayout = (num, defaultValue = "") => {
    const key = `img${num}`;
    return (
      <div
        key={key}
        className={`stackImg${num} img${num}`}
        style={styleCard}
      >
        <div style={styleRemoveIcon}>
          <Delete onClick={() => deleteElementsTypeOne(num, "img")} />
        </div>
        <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
          Imagem
        </Typography>
        {defaultValue ? (
          <img
            alt="preview"
            src={`${process.env.REACT_APP_BACKEND_URL}/public/${defaultValue}`}
            style={{ maxWidth: 150, marginBottom: 8 }}
          />
        ) : (
          <img alt="" style={{ maxWidth: 150, marginBottom: 8 }} />
        )}
        {!defaultValue && (
          <Button
            variant="contained"
            component="label"
            className={`btnImg${num}`}
          >
            Enviar Imagem
            <input
              type="file"
              accept="image/png, image/jpg, image/jpeg"
              hidden
              onChange={(e) => handleChangeMediasImg(e, num)}
            />
          </Button>
        )}
      </div>
    );
  };

  const audioLayout = (num, defaultValue = "", defaultRecord = "") => {
    const key = `audio${num}`;
    return (
      <div
        key={key}
        className={`stackAudio${num} audio${num}`}
        style={styleCard}
      >
        <div style={styleRemoveIcon}>
          <Delete onClick={() => deleteElementsTypeOne(num, "audio")} />
        </div>
        <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
          Áudio
        </Typography>
        {defaultValue && (
          <audio controls style={{ marginBottom: 8 }}>
            <source
              src={`${process.env.REACT_APP_BACKEND_URL}/public/${defaultValue}`}
            />
            Seu navegador não suporta HTML5
          </audio>
        )}
        {!defaultValue && (
          <Button
            variant="contained"
            component="label"
            className={`btnAudio${num}`}
            sx={{ marginBottom: 1 }}
          >
            Enviar Áudio
            <input
              type="file"
              accept="audio/ogg, audio/mp3, audio/opus"
              hidden
              onChange={(e) => handleChangeAudios(e, num)}
            />
          </Button>
        )}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Checkbox
            className={`checkaudio${num}`}
            defaultChecked={defaultRecord === "ok" ? false : true}
          />
          <Typography variant="body2">Enviar como áudio gravado na hora</Typography>
        </Stack>
      </div>
    );
  };

  const videoLayout = (num, defaultValue = "") => {
    const key = `video${num}`;
    return (
      <div
        key={key}
        className={`stackVideo${num} video${num}`}
        style={styleCard}
      >
        <div style={styleRemoveIcon}>
          <Delete onClick={() => deleteElementsTypeOne(num, "video")} />
        </div>
        <Typography variant="subtitle1" sx={{ marginBottom: 1 }}>
          Vídeo
        </Typography>
        {defaultValue && (
          <video controls style={{ width: 200, marginBottom: 8 }}>
            <source
              src={`${process.env.REACT_APP_BACKEND_URL}/public/${defaultValue}`}
            />
            Seu navegador não suporta HTML5
          </video>
        )}
        {!defaultValue && (
          <Button
            variant="contained"
            component="label"
            className={`btnVideo${num}`}
          >
            Enviar Vídeo
            <input
              type="file"
              accept="video/mp4, video/avi"
              hidden
              onChange={(e) => handleChangeVideos(e, num)}
            />
          </Button>
        )}
      </div>
    );
  };

  // ================================
  //  LÓGICA PARA UPLOAD/PREVIEW DE MÍDIAS
  // ================================
  const handleChangeMediasImg = (e, number) => {
    if (!e.target.files) return;
    if (e.target.files[0].size > 2000000) {
      toast.error("Arquivo maior que 2MB.");
      return;
    }
    const file = e.target.files[0];
    const blob = URL.createObjectURL(file);

    setPreviewImg(old => [...old, { number, url: blob, name: file.name }]);
    setMedias(old => [...old, file]);

    // Atualiza preview
    const container = document.querySelector(`.img${number} img`);
    if (container) container.src = blob;
    const btn = document.querySelector(`.btnImg${number}`);
    if (btn) btn.remove();
  };

  const handleChangeAudios = (e, number) => {
    if (!e.target.files) return;
    if (e.target.files[0].size > 5000000) {
      toast.error("Arquivo maior que 5MB.");
      return;
    }
    const file = e.target.files[0];
    const blob = URL.createObjectURL(file);

    setPreviewAudios(old => [...old, { number, url: blob, name: file.name }]);
    setMedias(old => [...old, file]);

    // Atualiza preview
    const audioTag = document.querySelector(`.audio${number} audio`);
    if (audioTag) {
      audioTag.src = blob;
    } else {
      // cria um <audio> se não existir
      const container = document.querySelector(`.audio${number}`);
      if (container) {
        const newAudio = document.createElement("audio");
        newAudio.controls = true;
        newAudio.style.marginBottom = "8px";
        const source = document.createElement("source");
        source.src = blob;
        newAudio.appendChild(source);
        container.insertBefore(newAudio, container.lastChild); // insere antes do checkbox
      }
    }
    const btn = document.querySelector(`.btnAudio${number}`);
    if (btn) btn.remove();
  };

  const handleChangeVideos = (e, number) => {
    if (!e.target.files) return;
    if (e.target.files[0].size > 20000000) {
      toast.error("Arquivo maior que 20MB.");
      return;
    }
    const file = e.target.files[0];
    const blob = URL.createObjectURL(file);

    setPreviewVideos(old => [...old, { number, url: blob, name: file.name }]);
    setMedias(old => [...old, file]);

    // Atualiza preview
    const videoTag = document.querySelector(`.video${number} video`);
    if (videoTag) {
      videoTag.src = blob;
    } else {
      const container = document.querySelector(`.video${number}`);
      if (container) {
        const newVid = document.createElement("video");
        newVid.controls = true;
        newVid.style.width = "200px";
        const source = document.createElement("source");
        source.src = blob;
        newVid.appendChild(source);
        container.insertBefore(newVid, container.lastChild);
      }
    }
    const btn = document.querySelector(`.btnVideo${number}`);
    if (btn) btn.remove();
  };

  // ================================
  //     DELETA UM ITEM/CARD
  // ================================
  const deleteElementsTypeOne = (id, type) => {
    setElementsSeq(old => old.filter(item => item !== `${type}${id}`));
    setElements(old => old.filter(el => el.key !== `${type}${id}`));

    if (type === "message") setNumberMessages(x => x - 1);
    if (type === "interval") setNumberInterval(x => x - 1);
    if (type === "img") setNumberImg(x => x - 1);
    if (type === "audio") setNumberAudio(x => x - 1);
    if (type === "video") setNumberVideo(x => x - 1);

    // Se quiser, remover do preview e da lista de medias
  };

  // =========================================
  //  FUNÇÃO GERA OBJ FINAL (type, value, etc)
  // =========================================
  const buildFinalSequence = (uploadedFiles) => {
    const finalData = [];

    const arrMsg = elementsSeq.filter(i => i.includes("message"));
    const arrInt = elementsSeq.filter(i => i.includes("interval"));
    const arrImg = elementsSeq.filter(i => i.includes("img"));
    const arrAud = elementsSeq.filter(i => i.includes("audio"));
    const arrVid = elementsSeq.filter(i => i.includes("video"));

    // Mensagem
    for (let i = 0; i < arrMsg.length; i++) {
      const id = arrMsg[i];
      const val = document
        .querySelector(`.${id}`)
        ?.querySelector(".MuiInputBase-input")?.value;
      if (!val) {
        toast.error("Campo de mensagem vazio.");
        throw "";
      }
      finalData.push({
        type: "message",
        value: val,
        number: id
      });
    }

    // Intervalo
    for (let i = 0; i < arrInt.length; i++) {
      const id = arrInt[i];
      const val = document
        .querySelector(`.${id}`)
        ?.querySelector(".MuiInputBase-input")?.value;
      const intervalNum = parseInt(val);
      if (!val || intervalNum <= 0 || intervalNum > 120) {
        toast.error("Intervalo inválido (0 ou maior que 120).");
        throw "";
      }
      finalData.push({
        type: "interval",
        value: val,
        number: id
      });
    }

    // Imagem
    for (let i = 0; i < arrImg.length; i++) {
      const id = arrImg[i];
      if (elementsSeqEdit.includes(id)) {
        // Já existia no edit
        const itemEdited = elementsEdit.find(e => e.number === id);
        finalData.push({
          type: "img",
          value: itemEdited.value,
          original: itemEdited.original,
          number: itemEdited.number
        });
      } else {
        // Novo
        const onlyImgs = uploadedFiles?.filter(
          f => f.includes("png") || f.includes("jpg") || f.includes("jpeg")
        );
        const onlyMedias = medias.filter(
          m => m.name.includes("png") || m.name.includes("jpg") || m.name.includes("jpeg")
        );
        finalData.push({
          type: "img",
          value: onlyImgs[i],
          original: onlyMedias[i]?.name,
          number: id
        });
      }
    }

    // Audio
    for (let i = 0; i < arrAud.length; i++) {
      const id = arrAud[i];
      const recordChecked = document
        .querySelector(`.check${id}`)
        ?.querySelector(".PrivateSwitchBase-input")?.checked;

      if (elementsSeqEdit.includes(id)) {
        const itemEdited = elementsEdit.find(e => e.number === id);
        finalData.push({
          type: "audio",
          value: itemEdited.value,
          original: itemEdited.original,
          number: itemEdited.number,
          record: recordChecked
        });
      } else {
        const onlyAudios = uploadedFiles?.filter(
          f => f.includes("mp3") || f.includes("ogg") || f.includes("mpeg") || f.includes("opus")
        );
        const onlyMedias = medias.filter(
          m =>
            m.name.includes("mp3") ||
            m.name.includes("ogg") ||
            m.name.includes("mpeg") ||
            m.name.includes("opus")
        );
        finalData.push({
          type: "audio",
          value: onlyAudios[i],
          original: onlyMedias[i]?.name,
          number: id,
          record: recordChecked
        });
      }
    }

    // Video
    for (let i = 0; i < arrVid.length; i++) {
      const id = arrVid[i];
      if (elementsSeqEdit.includes(id)) {
        const itemEdited = elementsEdit.find(e => e.number === id);
        finalData.push({
          type: "video",
          value: itemEdited.value,
          original: itemEdited.original,
          number: itemEdited.number
        });
      } else {
        const onlyVideos = uploadedFiles?.filter(
          f => f.includes("mp4") || f.includes("avi")
        );
        const onlyMedias = medias.filter(
          m => m.name.includes("mp4") || m.name.includes("avi")
        );
        finalData.push({
          type: "video",
          value: onlyVideos[i],
          original: onlyMedias[i]?.name,
          number: id
        });
      }
    }

    return finalData;
  };

  // ==================================
  //   VERIFICA CARDS VAZIOS DE MÍDIA
  // ==================================
  const verifyButtonsUpload = () => {
    const imgs = elementsSeq.filter(i => i.includes("img"));
    const auds = elementsSeq.filter(i => i.includes("audio"));
    const vids = elementsSeq.filter(i => i.includes("video"));

    for (let i = 0; i < imgs.length; i++) {
      const btn = document.querySelector(`.btn${capitalize(imgs[i])}`);
      if (btn) return true;
    }
    for (let i = 0; i < auds.length; i++) {
      const btn = document.querySelector(`.btn${capitalize(auds[i])}`);
      if (btn) return true;
    }
    for (let i = 0; i < vids.length; i++) {
      const btn = document.querySelector(`.btn${capitalize(vids[i])}`);
      if (btn) return true;
    }
    return false;
  };

  // ================================
  //   SALVA (CREATE OU EDIT)
  // ================================
  const handleSaveNode = async () => {
    setLoading(true);

    // Monta FormData com as mídias
    const formData = new FormData();
    medias.forEach(file => {
      if (!file) return;
      if (file.type?.startsWith("image")) {
        new Compressor(file, {
          quality: 0.7,
          success(compressed) {
            formData.append("medias", compressed);
            formData.append("body", compressed.name);
          },
          error(err) {
            console.log("Erro ao comprimir imagem:", err);
          }
        });
      } else {
        formData.append("medias", file);
        formData.append("body", file.name);
      }
    });

    // Cria pequeno delay para dar tempo de compressão
    setTimeout(async () => {
      const hasEmptyUploads = verifyButtonsUpload();
      if (hasEmptyUploads) {
        toast.error("Existe card de mídia sem upload concluído.");
        setLoading(false);
        return;
      }

      // Se não tem nenhuma mídia (ou se é edição e não atualizamos)
      const noMedia = (numberAudio + numberVideo + numberImg) === 0 || medias.length === 0;

      if (open === "edit") {
        if (noMedia) {
          // Apenas salva sem subir nada
          try {
            const mountData = {
              seq: elementsSeq,
              elements: buildFinalSequence(null)
            };
            onUpdate({ ...data, data: mountData });
            toast.success("Conteúdo atualizado com sucesso!");
            handleClose();
            setLoading(false);
            return;
          } catch (error) {
            setLoading(false);
            return;
          }
        }
        // Se tem mídia pra subir
        try {
          const res = await api.post("/flowbuilder/content", formData);
          const mountData = {
            seq: elementsSeq,
            elements: buildFinalSequence(res.data)
          };
          onUpdate({ ...data, data: mountData });
          toast.success("Conteúdo atualizado com sucesso!");
          handleClose();
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }

      } else {
        // CREATE
        if (noMedia) {
          try {
            const mountData = {
              seq: elementsSeq,
              elements: buildFinalSequence(null)
            };
            onSave(mountData);
            toast.success("Conteúdo criado com sucesso!");
            handleClose();
            setLoading(false);
            return;
          } catch (error) {
            setLoading(false);
            return;
          }
        }
        try {
          const res = await api.post("/flowbuilder/content", formData);
          const mountData = {
            seq: elementsSeq,
            elements: buildFinalSequence(res.data)
          };
          onSave(mountData);
          toast.success("Conteúdo criado com sucesso!");
          handleClose();
          setLoading(false);
        } catch (error) {
          console.log(error);
          setLoading(false);
        }
      }
    }, 1500);
  };

  // =============================
  //   FECHA O MODAL
  // =============================
  const handleClose = () => {
    close(null);
    setActiveModal(false);

    // Timeout para limpar tudo depois de fechar
    setTimeout(() => {
      setElements([]);
      setElementsSeq([]);
      setElementsEdit([]);
      setElementsSeqEdit([]);
      setMedias([]);
      setPreviewImg([]);
      setPreviewAudios([]);
      setPreviewVideos([]);

      setNumberMessages(0);
      setNumberMessagesLast(0);
      setNumberInterval(0);
      setNumberIntervalLast(0);
      setNumberImg(0);
      setNumberImgLast(0);
      setNumberAudio(0);
      setNumberAudioLast(0);
      setNumberVideo(0);
      setNumberVideoLast(0);
    }, 500);
  };

  // =============================
  //   AO ABRIR (CREATE/EDIT)
  // =============================
  useEffect(() => {
    if (open === "edit") {
      setLabels({ title: "Editar conteúdo", btn: "Salvar" });
      if (data) {
        const { seq, elements: els } = data.data;
        setElementsSeq(seq);
        setElementsSeqEdit(seq);
        setElementsEdit(els);

        seq.forEach((id) => {
          const item = els.find(e => e.number === id);
          if (!item) return;
          const numId = parseInt(id.replace(/\D+/g, "")); // extrai somente dígitos
          switch (item.type) {
            case "message":
              setElements(old => [...old, messageLayout(numId, item.value)]);
              setNumberMessages(n => {
                const arr = seq.filter(x => x.includes("message"))
                  .map(x => parseInt(x.replace("message", "")));
                setNumberMessagesLast(Math.max(...arr) + 1);
                return n + 1;
              });
              break;

            case "interval":
              setElements(old => [...old, intervalLayout(numId, item.value)]);
              setNumberInterval(n => {
                const arr = seq.filter(x => x.includes("interval"))
                  .map(x => parseInt(x.replace("interval", "")));
                setNumberIntervalLast(Math.max(...arr) + 1);
                return n + 1;
              });
              break;

            case "img":
              setElements(old => [...old, imgLayout(numId, item.value)]);
              setNumberImg(n => {
                const arr = seq.filter(x => x.includes("img"))
                  .map(x => parseInt(x.replace("img", "")));
                setNumberImgLast(Math.max(...arr) + 1);
                return n + 1;
              });
              break;

            case "audio":
              setElements(old => [
                ...old,
                audioLayout(numId, item.value, item.record ? "" : "ok")
              ]);
              setNumberAudio(n => {
                const arr = seq.filter(x => x.includes("audio"))
                  .map(x => parseInt(x.replace("audio", "")));
                setNumberAudioLast(Math.max(...arr) + 1);
                return n + 1;
              });
              break;

            case "video":
              setElements(old => [...old, videoLayout(numId, item.value)]);
              setNumberVideo(n => {
                const arr = seq.filter(x => x.includes("video"))
                  .map(x => parseInt(x.replace("video", "")));
                setNumberVideoLast(Math.max(...arr) + 1);
                return n + 1;
              });
              break;
            default:
              break;
          }
        });
      }
      setActiveModal(true);

    } else if (open === "create") {
      setLabels({ title: "Adicionar conteúdo ao fluxo", btn: "Adicionar" });
      setActiveModal(true);
    }
  }, [open, data]);

  // Limpa quando desmonta
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // =============================
  //   ESTILOS GENÉRICOS
  // =============================
  const styleCard = {
    border: "2px dashed var(--primaryColor)",
    background: "var(--primaryBackground)",
    color: "var(--primaryTextColor)",
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
    position: "relative"
  };

  const styleRemoveIcon = {
    position: "absolute",
    right: 8,
    top: 8,
    cursor: "pointer"
  };

  const styleButton = {
    width: "100%",
    height: 90,
    border: "2px dashed var(--primaryColor)",
    borderRadius: 8,
    background: "var(--primaryBackground)",
    color: "var(--primaryTextColor)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    margin: 0
  };

  // =============================
  //   RENDER
  // =============================
  return (
    <Dialog
      open={activeModal}
      fullWidth
      maxWidth="sm"
      scroll="paper"
    >
      {!loading && (
        <DialogTitle>{labels.title}</DialogTitle>
      )}

      <div style={{ padding: 16, display: loading ? "none" : "block" }}>
        {/* LISTA DOS CARDS (DRAG AND DROP) */}
        <DragDropContext onDragEnd={handleOnDragEnd}>
          <Droppable droppableId="droppable-cards">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                style={{
                  maxHeight: "40vh",
                  overflowY: "auto",
                  marginBottom: 16,
                  border: "1px solid #ccc",
                  borderRadius: 8,
                  padding: 8
                }}
              >
                {elements.map((item, index) => (
                  <Draggable
                    key={item.key}
                    draggableId={item.key}
                    index={index}
                  >
                    {(providedDrag) => (
                      <div
                        ref={providedDrag.innerRef}
                        {...providedDrag.draggableProps}
                        {...providedDrag.dragHandleProps}
                        style={{
                          userSelect: "none",
                          marginBottom: 8,
                          ...providedDrag.draggableProps.style
                        }}
                      >
                        {item}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {/* BOTÕES (CARDS) PARA CRIAR CADA ELEMENTO */}
        <Grid container spacing={1.5}>
          <Grid item xs={6} sm={4} md={3}>
            <div
              style={styleButton}
              onClick={() => {
                const newId = numberMessagesLast;
                setElementsSeq(old => [...old, `message${newId}`]);
                setElements(arr => [...arr, messageLayout(newId)]);
                setNumberMessages(n => n + 1);
                setNumberMessagesLast(x => x + 1);
              }}
            >
              <Message />
              <Typography variant="body2" sx={{ marginTop: 1 }}>
                Texto
              </Typography>
            </div>
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <div
              style={styleButton}
              onClick={() => {
                const newId = numberIntervalLast;
                setElementsSeq(old => [...old, `interval${newId}`]);
                setElements(arr => [...arr, intervalLayout(newId)]);
                setNumberInterval(n => n + 1);
                setNumberIntervalLast(x => x + 1);
              }}
            >
              <AccessTime />
              <Typography variant="body2" sx={{ marginTop: 1 }}>
                Intervalo
              </Typography>
            </div>
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <div
              style={styleButton}
              onClick={() => {
                const newId = numberImgLast;
                setElementsSeq(old => [...old, `img${newId}`]);
                setElements(arr => [...arr, imgLayout(newId)]);
                setNumberImg(n => n + 1);
                setNumberImgLast(x => x + 1);
              }}
            >
              <ImageIcon />
              <Typography variant="body2" sx={{ marginTop: 1 }}>
                Imagem
              </Typography>
            </div>
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <div
              style={styleButton}
              onClick={() => {
                const newId = numberAudioLast;
                setElementsSeq(old => [...old, `audio${newId}`]);
                setElements(arr => [...arr, audioLayout(newId)]);
                setNumberAudio(n => n + 1);
                setNumberAudioLast(x => x + 1);
              }}
            >
              <MicNone />
              <Typography variant="body2" sx={{ marginTop: 1 }}>
                Áudio
              </Typography>
            </div>
          </Grid>
          <Grid item xs={6} sm={4} md={3}>
            <div
              style={styleButton}
              onClick={() => {
                const newId = numberVideoLast;
                setElementsSeq(old => [...old, `video${newId}`]);
                setElements(arr => [...arr, videoLayout(newId)]);
                setNumberVideo(n => n + 1);
                setNumberVideoLast(x => x + 1);
              }}
            >
              <Videocam />
              <Typography variant="body2" sx={{ marginTop: 1 }}>
                Vídeo
              </Typography>
            </div>
          </Grid>
        </Grid>
      </div>

      {/* AÇÕES DO MODAL */}
      <DialogActions>
        {!loading && (
          <Button
            onClick={handleClose}
            variant="outlined"
            color="secondary"
          >
            {i18n.t("contactModal.buttons.cancel")}
          </Button>
        )}
        {!loading && (
          <Button
            onClick={handleSaveNode}
            variant="contained"
            color="primary"
          >
            {labels.btn}
          </Button>
        )}
      </DialogActions>

      {loading && (
        <div
          style={{
            padding: 16,
            minHeight: 300,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          <Typography variant="body1" style={{ marginBottom: 16 }}>
            Subindo arquivos e criando conteúdo...
          </Typography>
          <CircularProgress />
        </div>
      )}
    </Dialog>
  );
};

export default FlowBuilderSingleBlockModal;
